import { Theme } from "@amx/ux-twoui-theming";
import { HighContrastSelector, IBreadcrumbStyles, ILinkStyles, IStackStyles, IStyle, ITextStyles } from "@fluentui/react";
import { getFontRampStyle, HeadingTypes, TextTypes } from "../../styles/fonts";
import { IconName } from "../../styles/icons";
import { media } from "../../styles/media";
import { amcIconsFontFamily } from "../Icon/Icon";

export const mediaSmallSize = 16;
export const mediaLargeSize = 24;
export const crumbMargin = 4;
export const smallMediaMargin = 2;
export const largeMediaMargin = 8;
export const chevronSmallSize = 18;
export const chevronLargeSize = 24;
export const chevronMargin = 2;

const breadCrumbItemStyles: IStyle = {
    selectors: {
        [media.maxS]: {
            fontSize: "12px",
            lineHeight: "16px"
        },
        [media.minM]: {
            fontSize: "12px",
            lineHeight: "16px"
        },
        [media.minXl]: {
            fontSize: "14px",
            lineHeight: "20px"
        }
    }
};

export const getBreadcrumbStyles = (theme: Theme, customChevronStyling: boolean): Partial<IBreadcrumbStyles> => {
    let chevronStyling = {};
    if (customChevronStyling) {
        chevronStyling = {
            color: theme.palette.neutralSecondary,
            fontFamily: amcIconsFontFamily,
            selectors: {
                [media.maxS]: {
                    fontSize: chevronSmallSize,
                },
                [media.minM]: {
                    fontSize: chevronSmallSize,
                },
                [media.minL]: {
                    fontSize: chevronLargeSize,
                },
                [media.minXl]: {
                    fontSize: chevronLargeSize,
                }
            }
        };
    }
    return {
        chevron: {
            marginLeft: chevronMargin,
            marginRight: chevronMargin,
            ...chevronStyling
        },
        listItem: {
            height: "100%"
        },
        overflowButton: {
            height: "16px"
        },
        item: {
            ...breadCrumbItemStyles,
            ":hover": {
                cursor: "text"
            }
        },
        itemLink: breadCrumbItemStyles,
        list: {
            selectors: {
                "li:first-child > *:first-child": {
                    paddingLeft: "0px"
                }
            },
            alignItems: "center"
        }
    };
};

export const getCrumbLinkStyles = (theme: Theme, iconName?: IconName,): ILinkStyles => {
    const textandIconClass = `[class*=crumbTitle], .${iconName}`;
    return {
        root: {
            color: theme.palette.neutralSecondary,
            [textandIconClass]: {
                color: theme.palette.neutralSecondary
            },
            ":hover": {
                [textandIconClass]: {
                    backgroundColor: theme.palette.neutralLighter
                },
                textDecoration: "none"
            },
            ":active:hover": {
                [textandIconClass]: {
                    backgroundColor: theme.palette.neutralLight
                },
                textDecoration: "none"
            },
            ":focus": {
                textDecoration: "none"
            },
            [HighContrastSelector]: {
                [textandIconClass]: {
                    color: "LinkText"
                },
                ":focus": {
                    outline: "highlight auto 1px !important"
                }
            }
        }

    };

};

export const getCrumbTextStyle = (theme: Theme, variant: TextTypes | HeadingTypes): ITextStyles => {
    const fontRampStyles = getFontRampStyle(theme, variant);
    return {
        root: {
            fontSize: fontRampStyles.fontSize,
            fontWeight: fontRampStyles.fontWeight,
            lineHeight: fontRampStyles.lineHeight,
            fontFamily: fontRampStyles.fontFamily,
            color: theme.palette.neutralSecondary,
            selectors: fontRampStyles.selectors,
            overflow: "hidden",
            textOverflow: "ellipsis"
        }
    };
};

export const getCrumbStyles = (textOnly: boolean, firstTitleMaxWidth: undefined | number, lastTitleWidth: undefined | number): IStackStyles => {
    let marginLeft: number;
    textOnly ? marginLeft = crumbMargin : marginLeft = 0;
    return {
        root: {
            marginRight: crumbMargin,
            marginLeft: marginLeft,
            maxWidth: firstTitleMaxWidth,
            width: lastTitleWidth,
            alignItems: "center"
        }
    };
};

export const getCrumbImageStyles = (addExtraPadding: boolean): IStackStyles => {
    const mediaMarginRight = addExtraPadding ? largeMediaMargin : smallMediaMargin;
    return {
        root: {
            marginRight: mediaMarginRight,
            marginLeft: smallMediaMargin,
            marginTop: 0
        }
    };
};

export const getCrumbIconStyles = (addExtraPadding: boolean): IStackStyles => {
    const mediaMarginRight = addExtraPadding ? largeMediaMargin : smallMediaMargin;
    return {
        root: {
            marginRight: mediaMarginRight,
            marginLeft: smallMediaMargin,
            marginTop: 0
        }
    };
};
